import ApiService from '@/service'
import { FunctionUsedKeys, UsedKeys } from '@/types'
import { moduleActionContext } from '@/vuex'
import {
  ApiSchema,
  IrisLintError,
  PythonLintError,
  ScriptAnalysisResult,
  Token,
} from '@klarna/cbrms-policy-rules-manager-client-ts'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'
import uniq from 'lodash/uniq'

export const scriptAnalysisContext = (context: any) => moduleActionContext(context, scriptAnalysisModule)

export interface AnalyzeScriptPayload {
  script: string
  mode?: 'RULE' | 'USER_FUNCTION' | 'DECISION_BUILDER'
  analyzed_methods?: string[]
  api?: ApiSchema
}
export interface ScriptAnalysisState {
  inputs: Token[]
  helpers: Token[]
  lintErrors: PythonLintError[]
  frontendLintErrors: IrisLintError[]
  unusedInputs: Token[]
  isLinting: boolean
  isParseSuccessful: boolean
  extensionsLintErrors: (PythonLintError | IrisLintError)[]
  extensionsIsLinting: boolean
  extensionsParseSuccessful: boolean
}

const scriptAnalysisModule = defineModule({
  namespaced: true,
  state: {
    inputs: [],
    helpers: [],
    lintErrors: [],
    frontendLintErrors: [],
    unusedInputs: [],
    isLinting: false,
    isParseSuccessful: false,
    extensionsLintErrors: [],
    extensionsIsLinting: false,
    extensionsParseSuccessful: false,
  } as ScriptAnalysisState,

  getters: defineGetters<ScriptAnalysisState>()({
    /**
     * Returns
     * {
     *   inputs: array of inputs used in the script
     *   helpers: aray of helpers used in the script
     * }
     */
    usedKeys(state: ScriptAnalysisState): UsedKeys {
      return {
        helpers: uniq(state.helpers.map(el => el.string)),
        inputs: uniq(state.inputs.map(el => el.string)),
      }
    },
    functionUsedKeys(state: ScriptAnalysisState): FunctionUsedKeys {
      return {
        helpers: uniq(state.helpers.map(el => el.string)),
        inputs: uniq(
          state.inputs.map(el => {
            return { name: el.string, required: el.required! }
          })
        ),
      }
    },
    hasErrors(state: ScriptAnalysisState): boolean {
      return state.lintErrors.length > 0 && !state.isParseSuccessful
    },
    hasWarnings(state: ScriptAnalysisState): boolean {
      return (state.lintErrors.length > 0 || state.frontendLintErrors.length > 0) && state.isParseSuccessful
    },
    extensionsHaveErrors(state: ScriptAnalysisState): boolean {
      return state.extensionsLintErrors.length > 0 && !state.extensionsParseSuccessful
    },
    extensionsHaveWarnings(state: ScriptAnalysisState): boolean {
      return state.extensionsLintErrors.length > 0 && state.extensionsParseSuccessful
    },
  }),

  mutations: defineMutations<ScriptAnalysisState>()({
    SET_USED_KEY_TOKENS(
      state: ScriptAnalysisState,
      {
        declaredInputs,
        usedHelpers,
      }: {
        declaredInputs: Token[]
        usedHelpers: Token[]
      }
    ) {
      state.inputs = declaredInputs
      state.helpers = usedHelpers
    },
    SET_LINT_ERRORS(state: ScriptAnalysisState, payload: ScriptAnalysisResult) {
      state.lintErrors = payload.errors || []
      state.frontendLintErrors = payload.iris_lint_errors
    },
    SET_UNUSED_INPUTS(state: ScriptAnalysisState, unusedInputs: Token[]) {
      state.unusedInputs = unusedInputs
    },
    SET_LINTING_STATE(state: ScriptAnalysisState, status: boolean) {
      state.isLinting = status
    },
    SET_PARSE_SUCCESS(state: ScriptAnalysisState, status: boolean) {
      state.isParseSuccessful = status
    },
    SET_EXTENSIONS_LINT_ERRORS(state: ScriptAnalysisState, errors: (PythonLintError | IrisLintError)[]) {
      state.extensionsLintErrors = errors
    },
    SET_EXTENSIONS_LINTING_STATE(state: ScriptAnalysisState, status: boolean) {
      state.extensionsIsLinting = status
    },
    SET_EXTENSIONS_PARSE_SUCCESS(state: ScriptAnalysisState, status: boolean) {
      state.extensionsParseSuccessful = status
    },
    RESET_EXTENSIONS_LINT_STATE(state: ScriptAnalysisState) {
      state.extensionsLintErrors = []
      state.extensionsIsLinting = false
      state.extensionsParseSuccessful = false
    },
  }),

  actions: defineActions({
    async ANALYZE_SCRIPT(ctx, { script, api, analyzed_methods, mode }: AnalyzeScriptPayload) {
      const { commit } = scriptAnalysisContext(ctx)
      commit.SET_LINTING_STATE(true)
      const { data: result } = await ApiService.analyzeScript({
        script,
        api,
        mode,
        analyzed_methods,
      })

      commit.SET_LINT_ERRORS(result)
      commit.SET_PARSE_SUCCESS(result.parse_success)
      // If it failed parsing, the inputs/helpers used are not available.
      // Let's maintain possible old results in that case
      if (result.parse_success) {
        commit.SET_USED_KEY_TOKENS({
          declaredInputs: result.used_inputs,
          usedHelpers: result.used_helpers,
        })
      }

      const { errors, used_inputs } = result

      const unusedInputs = errors
        .filter(e => e.error_type === 'U100')
        .map(e =>
          used_inputs.find(
            token => token.col_offset + 1 === parseInt(e.column, 10) && token.lineno === parseInt(e.line, 10)
          )
        )
        .filter(token => !!token) as Token[]

      commit.SET_UNUSED_INPUTS(unusedInputs)
      commit.SET_LINTING_STATE(false)

      return result
    },

    async ANALYZE_EXTENSIONS_SCRIPT(ctx, script: string) {
      const { commit } = scriptAnalysisContext(ctx)
      commit.SET_EXTENSIONS_LINTING_STATE(true)

      try {
        const { data: result } = await ApiService.analyzeScript({
          script,
          mode: 'DECISION_BUILDER',
        })

        const allErrors = [...(result.errors || []), ...(result.iris_lint_errors || [])]
        commit.SET_EXTENSIONS_LINT_ERRORS(allErrors)

        const hasIrisErrors = (result.iris_lint_errors || []).length > 0
        const hasPylintErrors = (result.errors || []).some(
          (e: PythonLintError) => e.error_type.startsWith('E') || e.error_type.startsWith('F')
        )
        commit.SET_EXTENSIONS_PARSE_SUCCESS(result.parse_success && !hasIrisErrors && !hasPylintErrors)

        return result
      } finally {
        commit.SET_EXTENSIONS_LINTING_STATE(false)
      }
    },
  }),
})

export default scriptAnalysisModule
